import { useEffect } from 'react';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedPageTitle, LocalizedParagraph, RoutePaths, useLocalizeOffsetValues } from '@mews-commander/ui';
import { DurationService, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { LinkButton, Pictogram, Spinner, Stack } from '@optimus-web/core';
import { NewPublicFormWrapper } from '../style';

export interface AuthorizeActionProps {
    authorizationToken: string;
    redirectUrl?: string;
}

export function AuthorizeAction({
    authorizationToken,
    redirectUrl = '/Home/SignIn' satisfies RoutePaths,
}: AuthorizeActionProps) {
    const {
        mutate: authorizeAction,
        status,
        data,
    } = useRepositoryMutation('generalUsers', 'authorizeAction', {
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    useEffect(() => {
        authorizeAction({ authorizationToken });
    }, [authorizationToken, authorizeAction]);

    return (
        <NewPublicFormWrapper>
            <Stack spacing="500" vertical>
                {(status === 'idle' || status === 'pending') && <AuthorizeActionPending />}
                {status === 'success' && (
                    <AuthorizeActionSuccess
                        redirectUrl={redirectUrl}
                        tokenValidityTimeSpan={data.tokenValidityTimeSpan}
                    />
                )}
                {status === 'error' && <AuthorizeActionError redirectUrl={redirectUrl} />}
            </Stack>
        </NewPublicFormWrapper>
    );
}

function AuthorizeActionPending() {
    return (
        <>
            <Spinner size="large" />
            <Stack spacing="100" vertical>
                <LocalizedPageTitle textKey={TextKey.AuthorizeActionVerificationPendingTitle} />
                <LocalizedParagraph textKey={TextKey.AuthorizeActionVerificationPendingDescription} />
            </Stack>
        </>
    );
}

function AuthorizeActionSuccess({
    redirectUrl,
    tokenValidityTimeSpan,
}: Pick<AuthorizeActionProps, 'redirectUrl'> & { tokenValidityTimeSpan: string | null | undefined }) {
    const localizeOffsetValues = useLocalizeOffsetValues({
        format: 'long',
        timeUnits: ['days', 'hours', 'minutes', 'seconds'],
    });

    return (
        <>
            <Pictogram value="success" size="large" />
            <Stack spacing="300" vertical>
                <Stack spacing="100" vertical>
                    <LocalizedPageTitle textKey={TextKey.AuthorizeActionVerificationSuccessTitle} />
                    <LocalizedParagraph
                        textKey={TextKey.AuthorizeActionVerificationSuccessDescription}
                        substitutes={{
                            duration: localizeOffsetValues(DurationService.parse(tokenValidityTimeSpan ?? '')),
                        }}
                    />
                </Stack>
                <Stack>
                    <LinkButton href={redirectUrl}>
                        <LocalizedText textKey={TextKey.Continue} />
                    </LinkButton>
                </Stack>
            </Stack>
        </>
    );
}

function AuthorizeActionError({ redirectUrl }: Pick<AuthorizeActionProps, 'redirectUrl'>) {
    return (
        <>
            <Pictogram value="error" size="large" />
            <Stack spacing="300" vertical>
                <Stack spacing="100" vertical>
                    <LocalizedPageTitle textKey={TextKey.AuthorizeActionVerificationErrorTitle} />
                    <LocalizedParagraph textKey={TextKey.AuthorizeActionVerificationErrorDescription} />
                </Stack>
                <Stack>
                    <LinkButton href={redirectUrl}>
                        <LocalizedText textKey={TextKey.Continue} />
                    </LinkButton>
                </Stack>
            </Stack>
        </>
    );
}
